/** @format */

import React, {useCallback, useContext, useState} from 'react';
import moment from 'moment-business-days';

import {Checkbox, Input} from '@sttark/design-system';
import {formatMoney, formatShortDate} from './functions/format';

import {QuoteContext} from 'frontier-quoter';
import {useShippingEstimator} from './hooks/use_shipping_estimator';
import {AddressAutocomplete, SelectWithErrorHandler, useForm} from 'frontier-form';

export function ShippingEstimator() {
    const {order, handleChange} = useContext(QuoteContext);

    const {formData, onFormInputChange, onFormUpdate} = useForm({});

    const {data, isPending, error} = useShippingEstimator({order, ...formData});

    const shipDate = order.ship_due_formatted ? moment(order.ship_due_formatted) : null;

    // default to Ground method
    const groundDelivery = data?.rates?.find(({code}) => code === '03');

    const deliveredDate = groundDelivery
        ? shipDate.businessAdd(parseInt(groundDelivery.transitDays))
        : null;

    return (
        <div>
            <div>
                <div className="hstack font-label-sm">
                    <span>Estimated Ship</span>
                    <span className="flex-grow-1 border-bottom border-dark mx-2 mt-1"></span>
                    <span>Estimated Arrival</span>
                </div>
                <div className="hstack justify-content-between font-body-xl">
                    <span>{formatShortDate(shipDate)}</span>
                    <span>{formatShortDate(deliveredDate)}</span>
                </div>
            </div>
            <div className="form_property row p-0">
                <div className="m-0 form-group col-12">
                    <AddressAutocomplete
                        newestds={true}
                        placeholder="*Enter address to estimate shipping"
                        label="*Street Address"
                        className="form-control form-control-sm"
                        value={formData.address ?? ''}
                        error={false}
                        errorPath={'address1'}
                        onSelect={({address1, city, state, zip, country, region}) => {
                            onFormUpdate({
                                address: {$set: `${address1} ${city}, ${state} ${zip}`},
                                address1: {$set: address1},
                                city: {$set: city},
                                state: {$set: state},
                                zip: {$set: zip},
                                region: {$set: region},
                                country: {$set: country}
                            });
                        }}
                        onChange={onFormInputChange.bind(this, 'address')}
                    />
                </div>
            </div>

            <ShippingEstimatorResults
                error={error}
                data={data}
                isLoading={isPending}
                shipDate={shipDate}
            />
        </div>
    );
}

const ShippingEstimatorResults = ({error, isLoading, data, shipDate}) => {
    if (isLoading) return 'Loading...';
    if (error) return error.message;

    data = data?.rates;

    if (data && data.length > 0) {
        // Display the first of each shipping category we want to show in the quoter.
        const freight = data.find(estimate => estimate.name.includes('Freight'));
        const ground = data.find(estimate => estimate.name.includes('Ground'));
        const twoDay = data.find(estimate => estimate.name.includes('2nd Day'));
        const nextDay = data.find(estimate => estimate.name.includes('Next Day'));
        const worldWideExpedite = data.find(estimate =>
            estimate.name.includes('Worldwide Expedited')
        );
        const worldWideExpress = data.find(estimate => estimate.name.includes('Worldwide Express'));
        const estimates = [
            freight,
            ground,
            twoDay,
            nextDay,
            worldWideExpress,
            worldWideExpedite
        ].filter(e => e != null);

        if (estimates.length > 0) {
            return (
                <div className="border-y">
                    {estimates.map((estimate, i) => (
                        <div key={i} className="py-2">
                            <p className="font-label-md">{estimate.name}</p>
                            <span className="font-body-sm">{formatMoney(estimate.total)}</span>
                            <span className="font-body-sm float-end">
                                Arrives{' '}
                                {formatShortDate(
                                    moment(shipDate).businessAdd(parseInt(estimate.transitDays))
                                )}
                            </span>
                        </div>
                    ))}
                    <div className="py-2">See more options at checkout</div>
                </div>
            );
        } else if (estimates.length === 0) {
            return <h5>Freight Shipment Required</h5>;
        }
    }

    return null;
};
